import { useEffect } from 'react';
import { default as useEventSink } from './useEventSink';
import metaDataTransforms from '@sstdev/lib_metadata-transforms';
import { constants } from 'lib_ui-services';

const { Blockly } = metaDataTransforms;

const _p = { useEventSink };

export default function useWorkspaceActionSubscriptions({
    namespace,
    relation,
    metadataRecord,
    blocklyConfigurationInWorkspace,
    toolboxReady,
    workspace,
    useCaseDetail
}) {
    const [subscribe, publish, request] = _p.useEventSink();

    useEffect(() => {
        const unsubscribes = [
            subscribe({ verb: 'save', namespace, relation }, async () => {
                const result = await request(
                    {
                        'metadata:useCaseDetail': useCaseDetail,
                        [`${namespace}:${relation}`]: { ...metadataRecord, blockly: blocklyConfigurationInWorkspace }
                    },
                    { verb: 'create', namespace: 'metaui', relation: 'dataDictionary' }
                );
                if (result.errors && result.errors.length > 0) {
                    publish(
                        {
                            message: 'Save unsuccessful due to validation errors.',
                            addToList: false,
                            isError: true, //todo: gl2025 remove
                            variant: 'error',
                            noCancelOption: true
                        },
                        { verb: 'confirm', namespace: 'application', relation: 'user' }
                    );
                    return;
                }
                await request(
                    {
                        oldRecord: metadataRecord,
                        newRecord: { ...metadataRecord, blockly: blocklyConfigurationInWorkspace }
                    },
                    { verb: 'update', namespace, relation }
                );
                publish(
                    {
                        message: 'Saved successfully',
                        timeout: 1000,
                        addToList: false,
                        isError: false, //todo: gl2025 remove
                        variant: 'success',
                        type: constants.notificationTypes.TOAST
                    },
                    { verb: 'pop', namespace: 'application', relation: 'notification' }
                );
            }),
            // "close" verb is hijacked to clear the workspace (but keeps the record active!)
            subscribe({ verb: 'close', namespace, relation }, () => {
                if (toolboxReady && metadataRecord) {
                    workspace?.clear?.();
                }
            }),
            //"refresh" verb for reloading the original (stored) record
            subscribe({ verb: 'refresh', namespace, relation }, () => {
                if (toolboxReady && metadataRecord) {
                    workspace?.clear?.();
                    workspace?.clearUndo?.();
                    workspace?.trashcan?.emptyContents?.();
                    Blockly?.serialization?.workspaces?.load?.(metadataRecord.blockly, workspace);
                }
            }),
            subscribe({ verb: 'view', namespace: 'metaui', relation: 'validations' }, () => {
                //creating the data dictionary is what runs the validations
                //see /lib_metadata-transforms/packages/lib_metadata-transforms/src/dataDictionaryService/relationProperties.js
                //any validation errors are dispatch as error notifications. see line 95 "metaDataTransforms.setValidationReporter" above.
                publish(
                    {
                        'metadata:useCaseDetail': useCaseDetail,
                        [`${namespace}:${relation}`]: { ...metadataRecord, blockly: blocklyConfigurationInWorkspace }
                    },
                    { verb: 'create', namespace: 'metaui', relation: 'dataDictionary' }
                );
            }),
            subscribe({ verb: 'view', namespace: 'metaui', relation: 'dataDictionary', status: 'success' }, () => {
                // by blockly "view" should open a simpleDialog with a data Dictionary viewer
                // that viewer will send the success to trigger this call.
                // the result of this call should then populate that viewer.
                publish(
                    {
                        'metadata:useCaseDetail': useCaseDetail,
                        [`${namespace}:${relation}`]: { ...metadataRecord, blockly: blocklyConfigurationInWorkspace }
                    },
                    { verb: 'create', namespace: 'metaui', relation: 'dataDictionary' }
                );
            })
        ];

        return () => {
            unsubscribes.forEach(u => u());
        };
    }, [
        subscribe,
        request,
        publish,
        namespace,
        relation,
        metadataRecord,
        blocklyConfigurationInWorkspace,
        toolboxReady,
        workspace,
        useCaseDetail
    ]);
}

export const _private = _p;
